import { Button } from "@/components/common"
import { PlusIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { FC } from "react"
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form"
import { useApp } from "../../AppProvider"
import { useCreatePatient, useEditPatient } from "../../hooks/usePatients"
import { Patient } from "../../types/Patient"

interface PatientFormProps {
  patient?: Patient
  handleClose: () => void
}

interface PatientInput extends Patient {
  metadataArr: { key: string; value: string }[]
}

// objectToArray converts a metadata object to an array of key-value pairs
const objectToArray = (metadata: { [key: string]: string }) => {
  return Object.entries(metadata).map(([key, value]) => ({ key, value }))
}

// arrayToObject converts an array of key-value pairs to a metadata object
const arrayToObject = (metadataArr: { key: string; value: string }[]) => {
  return metadataArr.reduce(
    (acc, { key, value }) => {
      acc[key] = value
      return acc
    },
    {} as { [key: string]: string },
  )
}

export const PatientForm: FC<PatientFormProps> = ({ patient, handleClose }) => {
  const { token, addToast, orgId } = useApp()

  const {
    control,
    register,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<PatientInput>({
    defaultValues: {
      name: patient?.name || "",
      owner: patient?.owner || "",
      clientId: patient?.clientId || "",
      email: patient?.email || "",
      metadataArr: objectToArray(patient?.metadata ?? {}),
    },
  })
  const { fields, append, remove } = useFieldArray<any>({
    control,
    name: "metadataArr",
  })

  const createMutation = useCreatePatient(orgId, token)
  const editMutation = useEditPatient(orgId, token)
  const mutation = patient?._id ? editMutation : createMutation

  const onSubmit: SubmitHandler<PatientInput> = async ({ metadataArr, ...formData }) => {
    let newPatient = { ...patient, ...formData, metadata: arrayToObject(metadataArr) }
    try {
      let res = await mutation.mutateAsync(newPatient as any)

      if (res.error || res.errors || !res) {
        throw new Error(res.error ?? res.errors?.[0]?.message ?? "Unknown error")
      } else {
        reset()
        if (handleClose) handleClose()
      }
    } catch (error) {
      addToast("Server error", (error as Error).message, "error")
    }
  }

  return (
    <div className="w-screen max-w-md">
      <h2 className="text-lg font-semibold">{patient ? "Edit" : "Create"} Patient</h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset
          disabled={mutation.isPending}
          className="relative mt-3 flex w-full max-w-lg flex-col gap-y-4 disabled:animate-pulse disabled:opacity-80">
          <label className="label">
            Pet name
            <input
              {...register("name", { required: true })}
              type="text"
              placeholder="Pet name"
              className="input"
            />
          </label>
          <label className="label">
            Owner name
            <input
              {...register("owner", { required: true })}
              type="text"
              placeholder="Owner name"
              className="input"
            />
          </label>
          <label className="label">
            Client ID
            <input
              {...register("clientId", { required: true })}
              type="text"
              className="input border"
              placeholder="Client ID"
            />
          </label>
          <label className="label">
            Email
            <input {...register("email")} type="email" className="input" placeholder="Email (optional)" />
          </label>
          <div>
            <div className="label">Metadata</div>
            {fields.map((field, index) => (
              <div key={field.id} className="mt-1 flex gap-x-2">
                <input
                  className="input mt-0"
                  {...register(`metadataArr.${index}.key`)}
                  placeholder="Field"
                  type="text"
                />
                <input
                  className="input mt-0"
                  {...register(`metadataArr.${index}.value`)}
                  type="text"
                  placeholder="Value"
                />
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-900"
                  onClick={() => remove(index)}>
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => append({ key: "", value: "" })}
              className="btn-outline mt-2 w-fit">
              <PlusIcon className="mr-1 -ml-1 h-5 w-5" aria-hidden="true" />
              <span className="sr-only">Add</span>
              Field
            </button>
          </div>
          <div className="mt-6 flex justify-end gap-x-2">
            <Button
              type="button"
              className="btn-secondary"
              onClick={() => {
                reset()
                handleClose()
              }}
              disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" className="btn" disabled={!isDirty || mutation.isPending}>
              {mutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </fieldset>
      </form>
    </div>
  )
}
